'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { reportError } from '@core/lib/errorReporter';
import { ShieldIcon } from '@ui/components/dashboard/icons';

interface DashboardErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function DashboardError({ error, reset }: DashboardErrorProps) {
  useEffect(() => {
    reportError(error, { digest: error.digest, source: 'dashboard-error-boundary' });
  }, [error]);

  return (
    <div className="flex flex-1 items-center justify-center py-12">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-bg-2">
          <ShieldIcon className="text-text-2" />
        </div>
        <h1 className="mb-2 text-lg font-semibold text-text-0">Something went wrong</h1>
        <p className="mb-1 text-sm text-text-2">
          We couldn't load this part of the dashboard. The issue may be temporary.
        </p>
        {error.digest && (
          <p className="mb-4 font-mono text-[11px] text-text-3">Reference: {error.digest}</p>
        )}
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => reset()}
            className="inline-flex cursor-pointer items-center gap-[7px] rounded-[5px] border border-accent bg-accent px-3 py-[6px] text-xs font-semibold text-white transition-colors hover:border-accent-hover hover:bg-accent-hover"
          >
            Try again
          </button>
          <Link
            href="/dashboard/assistants"
            className="inline-flex cursor-pointer items-center gap-[7px] rounded-[5px] border border-border-2 bg-bg-3 px-3 py-[6px] text-xs font-semibold text-text-1 transition-colors hover:border-border-3 hover:bg-bg-hover"
          >
            Back to Assistants
          </Link>
        </div>
        <p className="mt-6 text-xs text-text-3">If this persists, contact your administrator.</p>
      </div>
    </div>
  );
}
